import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "zustand";

import { FeatureFlagsStore } from "../../store/featureFlags";
import { navigationStore } from "../../store/navigation";
import { isSuccess } from "../../utils/api.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import { useClientSideLazyLoad } from "../misc/useClientSideLazyLoad";

import { useGeneratedInsightsAction } from "./useGeneratedInsightsAction";
import { useGetInprogressGeneratedInsights } from "./useGetInprogressGeneratedInsights";

export interface GeneratedInsightsLoaderProps {
    isLoaderInView?: boolean;
}

export function useGeneratedInsightsLoader({
    isLoaderInView,
}: GeneratedInsightsLoaderProps): [
    AsyncStatus,
    GeneratedInsight[],
    number,
    GeneratedInsight[],
] {
    const [insights, setInsights] = useState<GeneratedInsight[]>([]);
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const identifier = useStore(navigationStore, (state) => state.identifier);
    const [insightsState, insightsAction] = useGeneratedInsightsAction();

    const fetchGeneratedInsights = useCallback(() => {
        void insightsAction.execute().then((fetchedInsights) => {
            setInsights(fetchedInsights);
        });
    }, [insightsAction, setInsights, identifier]);

    useEffect(() => {
        fetchGeneratedInsights();
    }, [fetchGeneratedInsights]);

    const [lazyLoadedInsights] = useClientSideLazyLoad(
        insights,
        isLoaderInView ?? false,
    );

    useGetInprogressGeneratedInsights({ fetchGeneratedInsights });

    useEffect(() => {
        if (isSuccess(insightsState.status)) {
            logInsightBrowseResults(insights.length, useUnifiedSearch);
        }
    }, [insights, insightsState.status]);

    return [
        insightsState.status,
        lazyLoadedInsights as GeneratedInsight[],
        insights.length,
        insights,
    ];
}
